import * as Factory from 'factory.ts'
import faker from 'faker'

import { CountryCodes } from '@webapp/core/entities/external/Country'
import { PaymentMethodTypes } from '@webapp/models/enums'
import {
  type Payment,
  type PaymentBill,
  type PaymentDetails,
} from '@webapp/models/Payment'
import { PaymentStatuses } from '@webapp/modules/payments'
import { ApprovalChainMock } from './approvalChain.factory'
import { CurrencyMock } from './currency.factory'
import { PaymentApproverMock } from './paymentApprover.factory'
import { PaymentMethodMock } from './paymentMethod.factory'
import { SimpleUserMock } from './user.factory'

export const PaymentMock = Factory.Sync.makeFactory<Payment>({
  id: Factory.each(() => faker.random.number()),
  uuid: Factory.each(() => faker.random.uuid()),
  status: PaymentStatuses.APPROVED,
  currency: Factory.each(() => CurrencyMock.build()),
  amount: Factory.each(() => faker.finance.amount()),
  vendor_name: Factory.each(() => faker.company.companyName()),
  payment_source_name: Factory.each(() => faker.company.companyName()),
  payment_source_type: null,
  vendor_payment_method_type: PaymentMethodTypes.PROCURIFY_ACH,
  external_payment_source_id: Factory.each(() => faker.random.number()),
  processing_date: Factory.each(() => faker.date.recent().toISOString()),
  created_at: Factory.each(() => faker.date.recent().toISOString()),
  creator: Factory.each(() => {
    const { id, email, firstName, lastName, profile_image } =
      SimpleUserMock.build()

    return {
      id,
      email,
      first_name: firstName,
      last_name: lastName,
      profile_image,
      user_id: id,
    }
  }),
  posted_date: null,
  direct_debit_bank_account_id: null,
  direct_debit_bank_account_mask: null,
  process_on_approval: false,
  vendor_payment_method_country_code: CountryCodes.UNITED_STATES_OF_AMERICA,
  vendor_payment_method_rail: null,
})

export const PaymentBillMock = Factory.Sync.makeFactory<PaymentBill>({
  id: Factory.each(() => faker.random.number()),
  group_id: Factory.each(() => faker.random.number()),
  vendor_id: Factory.each(() => faker.random.number()),
  vendor_name: Factory.each(() => faker.company.companyName()),
  due_date: Factory.each(() => {
    const lastMonth = new Date()
    const nextMonth = new Date()
    lastMonth.setMonth(lastMonth.getMonth() - 1)
    nextMonth.setMonth(lastMonth.getMonth() + 2)
    return faker.date.between(lastMonth, nextMonth).toISOString()
  }),
  invoice_date: Factory.each(() => {
    const lastMonth = new Date()
    const nextMonth = new Date()
    lastMonth.setMonth(lastMonth.getMonth() - 1)
    nextMonth.setMonth(lastMonth.getMonth() + 2)
    return faker.date.between(lastMonth, nextMonth).toISOString()
  }),
  invoice_number: Factory.each(() => `INV-${faker.random.number()}`),
  attachments: [],
  uuid: Factory.each(() => faker.random.uuid()),
  amount: Factory.each(() => faker.random.number()),
  currency_name: 'USD',
})

export const PaymentDetailsMock = Factory.Sync.makeFactory<PaymentDetails>({
  ...PaymentMock.build(),
  bills: [PaymentBillMock.build()],
  approver: Factory.each(() => PaymentApproverMock.build()),
  approval_chain: Factory.each(() => ApprovalChainMock.build()),
  next_approver_choices: [],
  vendor_payment_method: Factory.each(() => PaymentMethodMock.build()),
  conversion_rate: null,
  source_amount: null,
  source_currency: 'USD',
  fee_amount: null,
  fee_currency: null,
})
